import { useCallback, useMemo, useState } from 'react';

import { groupApi } from '@/api/services/groupApi';
import { userApi } from '@/api/services/userApi';
import type { ChinaAppSysUser } from '@/types/entities';

import { useAdminChanges } from '../adminChanges';
import { PickerDialog, type PickerOption } from '../PickerDialog';
import { useAdminList } from './adminList';
import { AdminSubList } from './AdminSubList';

/**
 * Mitglieder einer Gruppe (REQ-28 §3) — im Altclient gab es die Liste nicht, obwohl die API
 * sie seit immer anbietet (`GET groups/{id}/users`).
 */
export interface AdminGroupMembersListProps {
  parentId: number;
}

const COLUMNS = ['Name', 'E-Mail'] as const;

export function AdminGroupMembersList(props: AdminGroupMembersListProps) {
  const { parentId } = props;
  const changes = useAdminChanges();
  const load = useCallback(() => groupApi.getUsers(parentId), [parentId]);
  const { rows, loading, error, reload } = useAdminList(load);
  const [options, setOptions] = useState<PickerOption[] | null>(null);
  const [busy, setBusy] = useState(false);

  const taken = useMemo(() => rows.map((user) => user.Id), [rows]);

  const openPicker = async () => {
    setBusy(true);
    const all = await userApi.get();
    setOptions(
      all
        .filter((user) => !taken.includes(user.Id))
        .map((user) => ({
          id: user.Id,
          label: user.Name === null || user.Name === '' ? (user.EMail ?? '') : user.Name,
          description: user.EMail ?? undefined,
        })),
    );
    setBusy(false);
  };

  const change = async (run: () => Promise<unknown>) => {
    setBusy(true);
    await run();
    await reload();
    setBusy(false);
    changes.notifyNavigationChanged();
  };

  return (
    <>
      <AdminSubList<ChinaAppSysUser>
        title="Members"
        columns={COLUMNS}
        rows={rows}
        rowKey={(user) => user.Id}
        renderCells={(user) => (
          <>
            <td>{user.Name}</td>
            <td>{user.EMail}</td>
          </>
        )}
        loading={loading}
        error={error}
        onRetry={() => {
          void reload();
        }}
        busy={busy}
        onAdd={() => {
          void openPicker();
        }}
        onRemove={(user) => {
          void change(() => groupApi.removeUser(parentId, user.Id));
        }}
        emptyText="Keine Mitglieder"
        errorMessage="Die Mitglieder konnten nicht geladen werden."
      />
      <PickerDialog
        open={options !== null}
        title="Benutzer auswählen"
        options={options ?? []}
        emptyText="Alle Benutzer sind bereits in dieser Gruppe."
        onClose={() => {
          setOptions(null);
        }}
        onSubmit={async (ids) => {
          await change(async () => {
            for (const id of ids) await groupApi.addUser(parentId, id);
          });
        }}
      />
    </>
  );
}

export default AdminGroupMembersList;
